import { zodResolver } from '@hookform/resolvers/zod';
import { loginResponseSchema, registerRequestSchema } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { FormField } from '@/components/form-field';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { apiRequest } from '@/lib/api';
import { applyServerErrors } from '@/lib/forms';
import { sessionQuery } from '@/lib/session';

export const Route = createFileRoute('/_guest/register')({ component: RegisterPage });

type Values = z.input<typeof registerRequestSchema>;

function RegisterPage() {
  const { data: session } = useQuery(sessionQuery);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const form = useForm<Values>({ resolver: zodResolver(registerRequestSchema) });
  const register = useMutation({
    mutationFn: (body: Values) =>
      apiRequest('/api/auth/register', { method: 'POST', body, schema: loginResponseSchema }),
    onSuccess: async () => {
      await queryClient.resetQueries({ queryKey: sessionQuery.queryKey });
      await navigate({ to: '/' });
    },
    onError: (err) => applyServerErrors(err, form.setError),
  });

  if (session && !session.panel.registrationEnabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Registration is closed</CardTitle>
          <CardDescription>
            Ask an administrator of this panel to create an account for you.
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Button asChild variant="outline" className="w-full">
            <Link to="/login" search={{ redirect: undefined }}>
              Back to sign in
            </Link>
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create an account</CardTitle>
        <CardDescription>We&apos;ll send a link to confirm your email address.</CardDescription>
      </CardHeader>
      <form onSubmit={form.handleSubmit((v) => register.mutate(v))} noValidate>
        <CardContent className="grid gap-4">
          <FormField label="Email" error={form.formState.errors.email?.message}>
            <Input type="email" autoComplete="email" autoFocus {...form.register('email')} />
          </FormField>
          <FormField label="Username" error={form.formState.errors.username?.message}>
            <Input autoComplete="username" {...form.register('username')} />
          </FormField>
          <FormField
            label="Password"
            error={form.formState.errors.password?.message}
            description="At least 10 characters."
          >
            <Input type="password" autoComplete="new-password" {...form.register('password')} />
          </FormField>
        </CardContent>
        <CardFooter className="mt-6 flex-col gap-3">
          <Button type="submit" className="w-full" disabled={register.isPending}>
            {register.isPending && <Loader2 className="animate-spin" />} Create account
          </Button>
          <Link
            to="/login"
            search={{ redirect: undefined }}
            className="text-muted-foreground hover:text-foreground text-sm"
          >
            Already have an account? Sign in
          </Link>
        </CardFooter>
      </form>
    </Card>
  );
}
